import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { mockData } from '../mock';
import * as Icons from 'lucide-react';
import { X, Scroll } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - handles both relative and absolute paths
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/programs?published_only=true`);
      setPrograms(response.data);
    } catch (error) {
      setPrograms(mockData.programs);
    } finally {
      setIsLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const iconMap = {
      'utensils': Icons.Utensils,
      'home': Icons.Home,
      'book-open': Icons.BookOpen,
      'briefcase': Icons.Briefcase,
      'heart': Icons.Heart,
      'hand-helping': Icons.HandHeart,
      'church': Icons.Church,
      'users': Icons.Users,
      'heart-pulse': Icons.HeartPulse,
      'trending-up': Icons.TrendingUp,
      'flower-2': Icons.Flower2,
      'scroll': Scroll
    };
    const IconComponent = iconMap[iconName] || Icons.Circle;
    return IconComponent;
  };

  const displayPrograms = programs.length > 0 ? programs : mockData.programs;

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="programs-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8E2D0] via-[#F5F2E8] to-[#D1C3A2] py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#0A345E] mb-6">Our Services</h1>
          <p className="text-xl text-gray-700 leading-relaxed">
            Comprehensive programs designed to provide pathways to stability, hope, and transformation.
          </p>
        </div>
      </section>

      {/* Programs Grid - 3 per row */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="text-center py-12">Loading programs...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="programs-list">
              {displayPrograms.map((program) => {
                const IconComponent = getIcon(program.icon);
                return (
                  <Card
                    key={program.id}
                    className="hover:shadow-2xl transition-all duration-300 border-0 shadow-lg overflow-hidden group cursor-pointer"
                    onClick={() => setSelectedProgram(program)}
                    data-testid={`program-${program.id}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={resolveImageUrl(program.image)} 
                        alt={program.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A345E]/90 via-[#0A345E]/50 to-transparent"></div>
                      <div className="absolute bottom-3 left-3">
                        <div className="w-10 h-10 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-[#007A7A]" />
                        </div>
                      </div>
                    </div>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg mb-1 text-[#0A345E]">{program.title}</CardTitle>
                      <CardDescription className="text-sm text-gray-600 line-clamp-2">{program.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs text-[#007A7A] font-medium">Click to learn more</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Program Detail Modal */}
      {selectedProgram && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedProgram(null)}
        >
          <Card 
            className="max-w-2xl w-full bg-white shadow-2xl relative overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setSelectedProgram(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/90 hover:bg-gray-100 transition-colors z-10 shadow-md"
              data-testid="close-program-modal"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            
            {/* Program Image Header */}
            <div className="relative h-56 overflow-hidden">
              <img 
                src={resolveImageUrl(selectedProgram.image)} 
                alt={selectedProgram.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A345E]/90 to-transparent"></div>
              <div className="absolute bottom-4 left-6 right-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center">
                    {(() => {
                      const IconComponent = getIcon(selectedProgram.icon);
                      return <IconComponent className="w-6 h-6 text-[#007A7A]" />;
                    })()}
                  </div>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">{selectedProgram.title}</h2>
              </div>
            </div>
            
            <CardContent className="p-6">
              {/* Description */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[#007A7A] uppercase tracking-wide mb-2">Overview</h3>
                <p className="text-gray-700 leading-relaxed">{selectedProgram.description}</p>
              </div>
              
              {/* Details */}
              {selectedProgram.details && (
                <div>
                  <h3 className="text-sm font-semibold text-[#007A7A] uppercase tracking-wide mb-2">Program Details</h3>
                  <p className="text-gray-600 leading-relaxed">{selectedProgram.details}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Programs;
